"use client"

import {
  gachaEvmAddressSchema,
  getGachaPreferredToken,
} from "@anaconda/core/gacha/evm-chains"
import { supportsGachaEthPayment } from "@anaconda/core/gacha/evm-eth-payment"
import type { GachaEvmPurchase } from "@anaconda/core/gacha/evm-purchase"
import { t } from "@lingui/macro"
import { usePrivy } from "@privy-io/react-auth"
import { wrap } from "@reatom/core"
import { useMemo, useRef } from "react"
import {
  http,
  createPublicClient,
  createWalletClient,
  custom,
  defineChain,
  erc20Abi,
} from "viem"
import { gachaEthPaymentTracking } from "./gacha-eth-payment-tracking"
import { createEvmGachaExecution } from "./gacha-evm-execution"
import { requestGachaEvmBalanceRefresh } from "./gacha-evm-payment-balance"
import {
  gachaEvmPurchases,
  isGachaEvmPurchaseBlocking,
  requestGachaEvmOutcomeRefresh,
} from "./gacha-evm-purchase-state"
import { isGachaEvmAddressLinked } from "./gacha-evm-wallet-connection"
import { getGachaIdentityToken } from "./gacha-identity-token"
import { useGachaApiOptions } from "./use-gacha-api-options"
import { useGachaProfileWallet } from "./use-gacha-evm-wallets"
import { useGachaNetworkContext } from "./use-gacha-network"

export function useGachaEvmExecution() {
  const { user, authenticated } = usePrivy()
  const userId = authenticated ? user?.id : undefined
  const currentUserId = useRef(userId)
  currentUserId.current = userId
  const { wallets, ready, wallet, walletAddress } = useGachaProfileWallet()
  const walletLinked = isGachaEvmAddressLinked(
    user?.linkedAccounts,
    walletAddress,
  )
  const currentLinkedAccounts = useRef(user?.linkedAccounts)
  currentLinkedAccounts.current = user?.linkedAccounts
  const context = useGachaNetworkContext()
  const apiOptions = useGachaApiOptions()
  const chain = context.chain
  const currentWallets = useRef(wallets)
  currentWallets.current = wallets
  const currentEnvironment = useRef(context.environment)
  currentEnvironment.current = context.environment
  const execution = useMemo(() => {
    if (
      !chain ||
      !wallet ||
      !walletLinked ||
      !userId ||
      !context.environment ||
      !chain.operations.generatePack.available
    )
      return null
    const address = gachaEvmAddressSchema.parse(wallet.address)
    const rpc = createPublicClient({
      transport: http(chain.rpcUrl, { retryCount: 0, timeout: 10_000 }),
    })
    const viemChain = defineChain({
      id: chain.chainId,
      name: chain.name,
      nativeCurrency: chain.nativeCurrency,
      rpcUrls: { default: { http: [chain.rpcUrl] } },
    })
    const assertContext = () => {
      if (
        context.selection.selected() !== chain.chainId ||
        context.selection.environment() !== context.environment ||
        currentEnvironment.current !== context.environment ||
        currentUserId.current !== userId ||
        !isGachaEvmAddressLinked(currentLinkedAccounts.current, address) ||
        !currentWallets.current.some(
          (connected) =>
            connected.address.toLowerCase() === address.toLowerCase() &&
            connected.walletClientType === wallet.walletClientType,
        )
      )
        throw new Error("Original purchase wallet or network context changed")
    }
    // Capture the initiating chain-bound provider after explicit authorization.
    // Keep using it for the whole queue, never a newly preferred wallet/provider.
    let signingProvider:
      | Awaited<ReturnType<typeof wallet.getEthereumProvider>>
      | undefined
    const getSigningProvider = async () => {
      assertContext()
      const provider = await wrap(wallet.getEthereumProvider())
      assertContext()
      signingProvider ??= provider
      return signingProvider
    }
    const getSigningClient = async () => {
      const provider = await wrap(getSigningProvider())
      const client = createWalletClient({
        chain: viemChain,
        transport: custom(provider),
      })
      const chainId = await wrap(client.getChainId())
      if (chainId !== chain.chainId)
        throw new Error("Purchase signer chain mismatch")
      const accounts = await wrap(client.getAddresses())
      if (
        !accounts.some(
          (account) => account.toLowerCase() === address.toLowerCase(),
        )
      )
        throw new Error("Original purchase signer unavailable")
      assertContext()
      return { client, chainId }
    }
    let signingOwner: string | null = null
    const releaseSigning = () => {
      if (signingOwner && gachaEvmPurchases.signingOwner() === signingOwner) {
        gachaEvmPurchases.signingOwner.set(null)
        context.selection.purchasePending.set(false)
      }
      signingOwner = null
    }
    const purchaseRecords = new Map<string, GachaEvmPurchase>()
    const execution = createEvmGachaExecution({
      chain,
      environment: context.environment,
      apiOptions,
      ethPaymentTracking: gachaEthPaymentTracking,
      getIdentityToken: () =>
        getGachaIdentityToken({ userId, walletAddress: address }),
      wallet: {
        address,
        authorizeChain: async () => {
          assertContext()
          signingProvider = undefined
          // Direct EIP-7702 EOAs can originate ordinary pay transactions. Only
          // the exact delegation indicator is allowed, never arbitrary contract
          // code or known smart-envelope providers. The BFF still independently
          // verifies the direct payer/target/token/amount/memo and canonical receipt.
          const knownSmartWallet =
            wallet.walletClientType === "coinbase_smart_wallet" ||
            wallet.walletClientType === "base_account"
          const code = knownSmartWallet
            ? undefined
            : await wrap(rpc.getCode({ address }))
          const directDelegatedEoa =
            code && /^0xef0100[0-9a-fA-F]{40}$/.test(code)
          if (
            knownSmartWallet ||
            (code && code !== "0x" && !directDelegatedEoa)
          )
            throw new Error(
              t`Smart-account transaction envelopes are not supported. Use a direct Ethereum wallet.`,
            )
          if ("authorizeConnection" in wallet)
            await wrap(wallet.authorizeConnection())
          const provider = await wrap(wallet.getEthereumProvider())
          const client = createWalletClient({
            chain: viemChain,
            transport: custom(provider),
          })
          if ((await wrap(client.getChainId())) !== chain.chainId)
            await wrap(wallet.switchChain(chain.chainId))
          signingProvider = await wrap(getSigningProvider())
        },
        getChainId: async () => (await wrap(getSigningClient())).chainId,
        send: async (to, data, value) => {
          // Both preflight and signing validate the same pinned provider/account/chain.
          const { client } = await wrap(getSigningClient())
          return await wrap(
            client.sendTransaction({
              account: address,
              to,
              data,
              ...(value === undefined ? {} : { value }),
              chain: viemChain,
            }),
          )
        },
      },
      rpc: {
        getChainId: () => rpc.getChainId(),
        getFunds: async (owner, token, spender) => {
          const [tokenBalance, allowance, nativeBalance, decimals] = await wrap(
            Promise.all([
              rpc.readContract({
                address: token,
                abi: erc20Abi,
                functionName: "balanceOf",
                args: [owner],
              }),
              rpc.readContract({
                address: token,
                abi: erc20Abi,
                functionName: "allowance",
                args: [owner, spender],
              }),
              rpc.getBalance({ address: owner }),
              rpc.readContract({
                address: token,
                abi: erc20Abi,
                functionName: "decimals",
              }),
            ]),
          )
          return { tokenBalance, allowance, nativeBalance, decimals }
        },
        estimateCost: async (from, to, data, value) => {
          const [gas, gasPrice] = await wrap(
            Promise.all([
              rpc.estimateGas({
                account: from,
                to,
                data,
                ...(value === undefined ? {} : { value }),
              }),
              rpc.getGasPrice(),
            ]),
          )
          // Conservative preflight margin, not a promised exact fee/sponsorship.
          return gas * gasPrice * BigInt(2)
        },
        wait: async (hash) =>
          (
            await wrap(
              rpc.waitForTransactionReceipt({
                hash,
                confirmations: 2,
                timeout: 60_000,
              }),
            )
          ).status === "success",
      },
      onRecord: (record) => {
        gachaEvmPurchases.upsert(record)
        purchaseRecords.set(record.id, record)
      },
      onSigning: (signing) => {
        if (!signing) releaseSigning()
      },
      getPending: () =>
        gachaEvmPurchases
          .records()
          .some(
            (record) =>
              isGachaEvmPurchaseBlocking(record) &&
              record.environment === context.environment &&
              record.chain.chainId === chain.chainId &&
              record.walletAddress.toLowerCase() === address.toLowerCase(),
          ),
      refresh: (record) => {
        requestGachaEvmBalanceRefresh()
        requestGachaEvmOutcomeRefresh(record)
      },
    })
    return {
      ...execution,
      purchase: async (input: Parameters<typeof execution.purchase>[0]) => {
        execution.validatePurchase(input)
        if (gachaEvmPurchases.signingOwner() || context.selection.locked())
          throw new Error(
            "An Issued Pack or network switch is pending; wait before purchasing again",
          )
        signingOwner = globalThis.crypto.randomUUID()
        gachaEvmPurchases.signingOwner.set(signingOwner)
        // Lock before initiating-wallet authorization, not only after reservation.
        context.selection.purchasePending.set(true)
        purchaseRecords.clear()
        try {
          return await wrap(execution.purchase(input))
        } finally {
          releaseSigning()
          // Only purchases from this authorized queue get bounded delivery checks.
          for (const record of purchaseRecords.values()) {
            gachaEvmPurchases.deliveryTracking.track(record)
          }
        }
      },
    }
  }, [
    chain,
    wallet,
    walletLinked,
    userId,
    context.environment,
    context.selection,
    apiOptions,
  ])
  return {
    ready,
    execution,
    walletAddress: walletAddress ?? "",
    balanceAmount: null,
    isBalanceLoading: false,
    chain,
    tokenSymbol: chain ? getGachaPreferredToken(chain).symbol : "payment token",
    supportsEthPayment: supportsGachaEthPayment(chain),
    signing: context.selection.purchasePending(),
    // Durable blockers are scoped to the original wallet/network. Failed and
    // unverified payments recover without blocking a separately authorized pack.
    pending:
      context.selection.purchasePending() ||
      gachaEvmPurchases
        .records()
        .some(
          (record) =>
            isGachaEvmPurchaseBlocking(record) &&
            record.environment === context.environment &&
            record.chain.chainId === chain?.chainId &&
            record.walletAddress.toLowerCase() === walletAddress?.toLowerCase(),
        ),
  }
}
